<template>
  <div class="cybersource-page">
    <Loader v-if="showLoader" />
    <div v-show="showContainer" id="cybersource-unified-checkout" class="cybersource-page__container"></div>
    <div v-if="isError" class="cybersource-page-stub">
      <h2 class="cybersource-page-stub__title">Oops!</h2>
      <p class="cybersource-page-stub__text">Something went wrong.</p>
      <p class="cybersource-page-stub__text">Please try again or contact our support team.</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Loader from './components/Loader.vue';

export default {
  components: { Loader },

  async created() {
    await this.init();
  },

  data() {
    return {
      formReady: false,
      submitting: false,
      isError: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    requisites() {
      const { requisites } = this.flowData?.payload || {};

      if (!requisites) return {};
      if (typeof requisites === 'string') {
        try {
          return JSON.parse(requisites);
        } catch (e) {
          return {};
        }
      }

      return requisites;
    },

    sessionJwt() {
      return this.requisites.session_jwt || '';
    },

    clientLibrary() {
      return this.requisites.client_library || '';
    },

    clientLibraryIntegrity() {
      return this.requisites.client_library_integrity || '';
    },

    showContainer() {
      return this.formReady && !this.submitting && !this.isError;
    },

    showLoader() {
      return (!this.formReady && !this.isError) || this.submitting;
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),

    async init() {
      if (!this.sessionJwt || !this.clientLibrary) {
        this.isError = true;
        return;
      }

      try {
        await this.loadScript();
        await this.mountUnifiedCheckout();
      } catch (e) {
        console.error('Failed to initialize CyberSource Unified Checkout', e);
        this.isError = true;
      }
    },

    loadScript() {
      return new Promise((resolve, reject) => {
        if (this.hasScriptInBody(this.clientLibrary)) {
          resolve();
          return;
        }

        const scriptTag = document.createElement('script');
        scriptTag.src = this.clientLibrary;
        scriptTag.integrity = this.clientLibraryIntegrity;
        scriptTag.crossOrigin = 'anonymous';
        scriptTag.onload = () => resolve();
        scriptTag.onerror = e => reject(e);
        document.body.appendChild(scriptTag);
      });
    },

    hasScriptInBody(src) {
      return !!document.querySelectorAll(`[src="${src}"]`).length;
    },

    async mountUnifiedCheckout() {
      const client = await window.VAS.UnifiedCheckout(this.sessionJwt);
      const checkout = await client.createCheckout({ autoProcessing: false });

      this.formReady = true;
      await this.$nextTick();

      const transientToken = await checkout.mount('#cybersource-unified-checkout');

      await this.submitToken(transientToken);
    },

    async submitToken(transientToken) {
      this.submitting = true;

      try {
        const { status } = await this.confirmPaymentInvoice({
          id: this.paymentInvoice.id,
          data: { payload: { transient_token: transientToken } },
        });

        if (status !== 200) {
          this.submitting = false;
          this.isError = true;
        }
      } catch (e) {
        console.error('Failed to confirm CyberSource payment', e);
        this.submitting = false;
        this.isError = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cybersource-page {
  display: flex;
  justify-content: center;
  align-items: center;

  &__container {
    width: 100%;
  }

  &-stub {
    &__title {
      margin: 0 0 5px;
    }

    &__text {
      margin: 0;
    }
  }
}
</style>
